<template>
  <teleport to="#app" ref="teleportElement">
    <div
      ref="rootElement"
      :style="{ top: `${top}px`, left: `${left}px` }"
      class="inline-popover"
    >
      <div class="input-autogrowarea inline-popover__input" style="z-index: 1">
        <div class="input-autogrowarea__sizer">{{ state.searchString }}&nbsp;</div>
        <input
          ref="searchInputElement"
          v-model="state.searchString"
          type="text"
          size="1"
          class="input-autogrowarea__input p-0"
          @keydown.up.prevent="optionsElement?.onKeyUp($event)"
          @keydown.down.prevent="optionsElement?.onKeyDown($event)"
          @keydown.backspace="onSearchRemove($event)"
          @keydown.escape.prevent="close()"
          @keydown.enter.prevent="onConfirm()"
        />
        <span v-if="marker" class="inline-popover__marker">{{ marker }}</span>
      </div>

      <input-dropdown-options
        ref="optionsElement"
        class="inline-popover__options"
        :options="items"
        :ordered-keys="items.map((_item:unknown, index) => `${index}`)"
        :group-key="'type'"
        :selected-key="state.selectedKey"
        :search-string="state.searchString"
        :prop="prop"
        @update:selected-key="state.selectedKey = ($event ?? undefined)"
        @select="onConfirm()"
        @close="close()"
        @focus-out="searchInputElement?.focus?.()"
        @keydown="onOptionsKeydown"
        @keydown.backspace.prevent="close()"
      >
        <template #default="{ item, itemKey }">
          <slot :item="item" :item-key="itemKey"></slot>
        </template>
        <template #grouplabel="{ value: itemType }">
          <div class="options-list__grouplabel">
            {{ $t(`objectNames.${itemType}`, 3) }}
          </div>
        </template>
      </input-dropdown-options>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted, onUnmounted } from "vue";
import { onClickOutside } from "@vueuse/core";

import InputDropdownOptions from "./InputDropdownOptions.vue";
import { ensureInt } from "@/helpers/utility/Type";

const props = withDefaults(defineProps<{
  left?: number;
  top?: number;
  items?: { [key: string]: unknown }[] | unknown[];
  marker?: string;
  prop?: string;
}>(), {
  left: 0,
  top: 0,
  items: () => [],
  marker: "@",
  prop: "title"
});

const emit = defineEmits(["close", "select"]);

const searchInputElement = ref<null | HTMLInputElement>(null);
const rootElement = ref<null | HTMLElement>(null);
const optionsElement = ref<null | InstanceType<
  typeof InputDropdownOptions
>>(null);
const teleportElement = ref<null | HTMLElement>(null);

let removeOutsideClickListener: undefined | (() => void) = undefined;

const state = reactive({
  selectedKey: undefined as undefined | string | number,
  searchString: ""
});

onMounted(() => {
  removeOutsideClickListener = onClickOutside(rootElement.value, () => {
    close(false);
  });

  searchInputElement.value?.focus();

  // add teleportParent to rootElement
  if (rootElement.value) {
    Object.assign(rootElement.value, {
      teleportParent: teleportElement.value?.parentElement
    });
  }
});

function onConfirm() {
  if (state.selectedKey == null) {
    return;
  }
  emit("select", props.items[ensureInt(state.selectedKey)]);
  close(false);
}

function close(resetFocus = true) {
  if (removeOutsideClickListener) {
    removeOutsideClickListener();
    removeOutsideClickListener = undefined;
  }

  emit("close", resetFocus);
}

onUnmounted(() => {
  if (removeOutsideClickListener) {
    removeOutsideClickListener();
  }
});

/**
 * Close the popover on backspace if search is empty
 * @param keyEvent
 */
function onSearchRemove(keyEvent: KeyboardEvent) {
  if (state.searchString == "") {
    keyEvent.preventDefault();
    close();
  }
}

/**
 * Handle any keyboard events of the options list
 * @param keyEvent
 */
function onOptionsKeydown(keyEvent: KeyboardEvent) {
  // make sure we always jump to the search input if a letter
  // is pressed while in the options list
  const isLetter = /^[a-zA-Z0-9]$/g;
  if (isLetter.test(keyEvent.key)) {
    searchInputElement.value?.focus?.();
  }
}
</script>

<style lang="scss">
.inline-popover {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: flex-start;
  pointer-events: none;

  &__input {
    border: none;
    padding: 1px 0.5em;
    padding-left: 1.25em;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
    outline: 1px solid var(--color-action);
    background-color: var(--color-background);
    font-size: 11px;
    margin: 0 2px;
    min-width: 2rem;
    pointer-events: auto;

    input,
    .input-autogrowarea__sizer {
      background-color: transparent;
      min-height: 1em;
    }

    &:focus {
      outline: none;
    }
  }
  &__marker {
    position: absolute;
    top: 0;
    left: 3px;
    font-size: 11px;
    line-height: 16px;
    padding: 1px;
  }
  &__options {
    pointer-events: auto;
  }
}
</style>
